import VisibilityOff from '@mui/icons-material/VisibilityOffRounded';
import Visibility from '@mui/icons-material/VisibilityRounded';
import { Box, List, TextField, Typography } from '@mui/material';
import * as R from 'ramda';
import { memo, useEffect, useState } from 'react';
import LangEnum from '../../lang/LangEnum';
import { ClarityEvents } from '../../lib/clarity/types/clarityEvents.types';
import { withEvent } from '../../lib/clarity/utils/captureEvent.utils';
import { useTranslation } from '../../modules/translation/TranslationContext';
import useTeceoStyles from '../../useTeceoStyles';
import { HideColumnsTestIds } from '../../utils/testIds/HideColumns.testIds';
import useColumnsVisibilityStyles from './columnsVisibility.styles';
import SavableHideableColumnsVisibilityFooter from './components/SavableHideableColumnsVisibilityFooter/SavableHideableColumnsVisibilityFooter';
import SavableHideableColumnsVisibilityHeader from './components/SavableHideableColumnsVisibilityHeader/SavableHideableColumnsVisibilityHeader';
import useSavableHideableColumnsVisibility from './hooks/useSavableHideableColumnsVisibility';
import { Column } from './hooks/useSavableHideableColumnsVisibility.types';
import { SavableHideableColumnsVisibilityProps } from './savableHideableColumnsVisibility.types';

import ListItemButton from '@mui/material/ListItemButton';

// TODO: testar em todas as telas que usam esse componente!!!
/**
 * @deprecated Use `ViewSettingsControl`
 * (src/components/FilterV3/components/ColumnsVisibility) on ListScreen pages.
 * This component remains for table-header-triggered listing pages not yet migrated.
 */
const SavableHideableColumnsVisibility = ({
  list,
  columns,
  onClose,
  onRestoreToDefaultColumns,
  canRestoreToDefaultColumns,
}: SavableHideableColumnsVisibilityProps) => {
  const { translate } = useTranslation();
  const { classes } = useColumnsVisibilityStyles();
  const { classes: textStyle } = useTeceoStyles();

  // TODO: utilizar useOptimistic do React 19
  const [internalColumns, setInternalColumns] = useState<Column[]>(columns);

  useEffect(() => {
    setInternalColumns(columns);
  }, [columns]);

  const {
    textValue,
    filteredColumns,
    changeTextValue,
    canRestoreToDefaultColumns: internalCanRestoreToDefaultColumns,
    canSaveColumns,
    isSavingColumns,
    saveColumns,
  } = useSavableHideableColumnsVisibility({
    list,
    columns: internalColumns,
  });

  const handleRestoreToDefaultColumns = () => {
    if (onRestoreToDefaultColumns) {
      onRestoreToDefaultColumns();
      return;
    }

    setInternalColumns(
      columns.map((column) => ({
        ...column,
        isVisible: R.isNil(column.showByDefault) ? true : column.showByDefault,
      })),
    );
  };

  const handleSaveAndApply = () => {
    saveColumns();
    onClose();
  };

  const handleOnClickListItem = (column: Column) => {
    if (column.toggleVisibility) {
      column.toggleVisibility(!column.isVisible);
    }

    setInternalColumns((internalColumns) => {
      return internalColumns.map((internalColumn) => ({
        ...internalColumn,
        isVisible:
          column.key === internalColumn.key ? !internalColumn.isVisible : internalColumn.isVisible,
      }));
    });
  };

  return (
    <Box test-id="savable-hideable-columns-visibility" className={classes.filterPaper}>
      <Box
        paddingTop={3 / 2}
        paddingBottom={0}
        paddingX={2}
        display="flex"
        flexDirection="column"
        rowGap="12px"
        className={classes.headerWrapper}
      >
        <SavableHideableColumnsVisibilityHeader onClose={onClose} />

        <TextField
          variant="outlined"
          value={textValue}
          onChange={(e) => changeTextValue(e.target.value)}
          label={translate(LangEnum.GLOBAL_SEARCH_COLUMNS)}
          size="small"
        />
      </Box>
      <List className={classes.content}>
        {filteredColumns.map((column) => {
          return (
            <ListItemButton
              key={column.key}
              disabled={R.isNil(column.canHide) ? false : !column.canHide}
              test-id={`${HideColumnsTestIds.COLUMN_ITEM_PREFIX}${column.key}`}
              onClick={withEvent(
                `${ClarityEvents.TOGGLE_COLUMN_VISIBILITY_WITH_NAME} ${column.name} to ${
                  column.isVisible ? 'hidden' : 'visible'
                }`,
                () => handleOnClickListItem(column),
              )}
            >
              <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                <Typography
                  color={column.isVisible ? 'textPrimary' : 'textSecondary'}
                  className={textStyle.lowercase}
                >
                  {column.name}
                </Typography>
                <Box marginLeft={1}>
                  {column.isVisible ? (
                    <Visibility
                      fontSize="small"
                      color={column.canHide === false ? 'disabled' : 'primary'}
                    />
                  ) : (
                    <VisibilityOff fontSize="small" />
                  )}
                </Box>
              </Box>
            </ListItemButton>
          );
        })}
      </List>
      <Box className={classes.footer}>
        <Box padding={3 / 2}>
          <SavableHideableColumnsVisibilityFooter
            onSaveColumns={handleSaveAndApply}
            canSaveColumns={canSaveColumns}
            isSavingColumns={isSavingColumns}
            onRestoreToDefaultColumns={handleRestoreToDefaultColumns}
            canRestoreToDefaultColumns={
              canRestoreToDefaultColumns || internalCanRestoreToDefaultColumns
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default memo(SavableHideableColumnsVisibility);
